import {
  audienceForPlan,
  visibleForRole,
  resolveCurrentRoleFromState,
} from "src/utils/plan-audience";
import { normalizePlan } from "./actions";

const isRetailerPlan = (p) => /retailer/i.test(p?.name || "");
const isVendorPlan = (p) => /vendor|buy\s*&\s*sell/i.test(p?.name || "");
const ANALYTICS_CODES = [
  "data-analytics",
  "analytics",
  "data-tools-analytics",
  "data-tools",
  "ai-dashboard",
  "ai-insights",
];
const typeCode = (p) => p.type_code || p.type?.code || "";

export default {
  availableUserTypes: (_s) => [],
  hasTypeGating: (_s, g) => g.availableUserTypes.length > 0,

  filteredMembershipPlans: (state) => {
    const role = resolveCurrentRoleFromState(state);
    const all = (state.allPlans || []).filter(
      (p) => (p.type_code || p.type?.code) === "membership"
    );
    return all.filter((p) => visibleForRole(audienceForPlan(p), role));
  },

  filteredAddonPlans: (state) => {
    const role = resolveCurrentRoleFromState(state);
    const all = (state.allPlans || []).filter(
      (p) => (p.type_code || p.type?.code) === "add-on"
    );
    return all.filter((p) => visibleForRole(audienceForPlan(p), role));
  },

  dataPlan: (s, g) =>
    (g.dataPlans || []).find((p) => p.id === s.dataPlanId) || null,

  dataPlans: (state) => {
    const realPlans = (state.allPlans || []).filter(
      (p) =>
        (p.type_code || p?.type?.code) === "add-on" &&
        (p.hub_addons || []).some((h) => ANALYTICS_CODES.includes(h.code))
    );

    const permanentComingSoonPlans = [
      {
        id: "ai-dashboard-plan",
        type: { id: 2, code: "add-on", name: "AI Analytics Hub" },
        type_code: "add-on",
        name: "AI Dashboards",
        is_free: false,
        amount: 49,
        price: 49,
        is_public: true,
        hub_addons: [
          {
            id: "ai-dashboard-addon",
            name: "AI Dashboard",
            code: "ai-dashboard",
            pivot: {
              pricing_plan_id: "ai-dashboard-plan",
              hub_addon_id: "ai-dashboard-addon",
            },
          },
        ],
        features: [
          "AI-powered predictive analytics",
          "Smart inventory forecasting",
          "Automated trend detection",
          "Custom AI-generated reports",
          "Market intelligence insights",
        ],
        category: "analytics",
        billing_frequency: "recurring",
        billing_recurrence: "month",
        is_free_trial: true,
        free_trial_length: 14,
        free_trial_period: "day",
        coming_soon: true,
        ai_features: true,
      },
      {
        id: "ai-insights-plan",
        type: { id: 2, code: "add-on", name: "AI Insights Hub" },
        type_code: "add-on",
        name: "AI Dashboards + Ask GL",
        is_free: false,
        amount: 99,
        price: 99,
        is_public: true,
        hub_addons: [
          {
            id: "ai-insights-addon",
            name: "AI Insights",
            code: "ai-insights",
            pivot: {
              pricing_plan_id: "ai-insights-plan",
              hub_addon_id: "ai-insights-addon",
            },
          },
        ],
        features: [
          "Everything in AI Dashboards",
          "Ask GL: Natural language queries",
          "AI-powered business recommendations",
          "Predictive market analysis",
          "Custom AI business advisor",
          "Voice-activated insights",
        ],
        category: "analytics",
        billing_frequency: "recurring",
        billing_recurrence: "month",
        is_free_trial: true,
        free_trial_length: 14,
        free_trial_period: "day",
        coming_soon: true,
        ai_features: true,
        is_popular: true,
      },
    ];

    const developmentMockData = [
      {
        id: 7, // mock data for free data plan
        type: { id: 2, code: "add-on", name: "Analytics Hub" },
        type_code: "add-on",
        name: "Live Data Hub",
        is_free: true,
        amount: 0,
        price: 0,
        is_public: true,
        hub_addons: [
          {
            id: 1,
            name: "Data Tools",
            code: "data-tools",
            pivot: { pricing_plan_id: 7, hub_addon_id: 1 },
          },
        ],
        features: [
          "Real-time inventory tracking",
          "Basic sales analytics",
          "Standard compliance reports",
          "Export data to CSV/Excel",
        ],
        category: "analytics",
        billing_frequency: "one-time",
        billing_recurrence: "month",
        is_free_trial: false,
        coming_soon: false,
        is_recommended: false,
      },
    ];

    return [...realPlans, ...permanentComingSoonPlans].map(normalizePlan);
  },

  basePlan: (s) =>
    (s.allPlans || []).find((p) => p.id === s.basePlanId) || null,

  hubPlans: (s) => {
    const ids = Array.isArray(s.hubPlanIds) ? s.hubPlanIds : [];
    const all = Array.isArray(s.allPlans) ? s.allPlans : [];
    return ids.map((id) => all.find((p) => p.id === id)).filter(Boolean);
  },

  monthlyTotal: (s, g) => {
    const selType = (s.userType || s.companyType || "").toLowerCase();
    const priceFor = (p) => {
      if (!p || p.is_free) return 0;
      const per = p.per_type_rates || {};
      const fromPer = per[selType];
      const base = Number(p.price ?? p.amount ?? 0);
      const v = Number.isFinite(fromPer) ? fromPer : base;
      return Number.isFinite(v) ? v : 0;
    };
    return [g.basePlan, ...g.hubPlans]
      .filter(Boolean)
      .reduce((sum, p) => sum + priceFor(p), 0);
  },
  wizardMonthlyTotal: (s, g) => {
    const selType = (s.userType || s.companyType || "").toLowerCase();
    const priceFor = (p) => {
      if (!p || p.is_free) return 0;
      const per = p.per_type_rates || {};
      const fromPer = per[selType];
      const base = Number(p.price ?? p.amount ?? 0);
      const v = Number.isFinite(fromPer) ? fromPer : base;
      return Number.isFinite(v) ? v : 0;
    };
    const data = priceFor(g.dataPlan);
    const total = g.monthlyTotal + data;
    return Number.isFinite(total) ? total : 0;
  },
  availableUserTypes: (s) => {
    // Collect role types that the backend actually priced for
    const roles = new Set();
    (s.allPlans || []).forEach((p) => {
      (p.rates_by_user || []).forEach((r) => {
        const t = String(r.type || "")
          .trim()
          .toLowerCase(); // 'retail'|'cultivator'|'manufacturer'
        if (t === "retail") roles.add("retailer");
        if (t === "cultivator") roles.add("cultivator");
        if (t === "manufacturer") roles.add("manufacturer");
      });
    });
    return Array.from(roles);
  },
  hasTypeGating: (s, g) => {
    // Show Step 1 iff we don’t already know the type AND there’s at least one typed price in payload
    if (s.userType) return false;
    if (s.companyType) return false; // you derived this from licenses in hydratePricing
    return g.availableUserTypes.length > 0;
  },
};
